// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';

import Modal from '../Modal';
import '../../../styles/import.css';
import SelectUser from '../../SelectUser';
import CodeEditor from '../../CodeEditor';
import SelectStack from '../../SelectStack';
import Sortable from '../../Sortable';

const SCAN_TEMPLATE = "# The Desktop sync app will run the function below which needs to scan for\n# appropriate files in the given folder and return the list of relative paths\n# from the folder to upload.\n\n# If the file exists in Stax.ai and has not been modified, it will not be\n# uploaded even if you return it from the function.\n\ndef run(folder):\n    files = []\n    for file in os.listdir(folder):\n        files.append(os.path.join(folder, file))\n    \n    return files \n";
const CLEANUP_TEMPLATE = "# The Desktop sync app will run the function below to handle any file cleanup\n# after uploading a file to Stax.ai. This function needs to return a boolean\n# value stating if cleanup was successfull.\n\n# If no cleanup is needed, simply return true\n\ndef run(file_path):\n    return True"


class ConnectDesktop extends Modal {

    state = {
        configurations: [],
        syncUser: '',
        config: {},
        edit: -1, // The index of the config we are changing...  
    }

    componentDidMount = async () => {
        this.props.loading(2000); 
        const load = async () => {
            if(!this.props.team || !this.props.team.sync)
                return setTimeout(load, 500);
            await this.loadConfig(); 
        }
        load(); 
    }

    loadConfig = async () => {
        await this.props.loadStacks();
        const { connectionIdx } = this.props.data; 
        const connection = connectionIdx >= 0? this.props.team?.sync?.desktop?.[connectionIdx]: {};
        this.setState({
            configurations: connection?.folders??[],
            syncUser: connection?.user??'', 
            root: connection?.root??'' 
        });
        this.props.loaded();
    }

    saveConfig = async() => {
        this.props.loading(9999); 
        const { connectionIdx } = this.props.data; 
        const connections = this.props.team?.sync?.desktop ?? [];
        const existing = connectionIdx >= 0 && connections.length > connectionIdx; 
        const connection = existing ? connections[connectionIdx]: {
            enabled: true, 
            user: this.props.user?._id, 
            syncUser: this.state.syncUser,
            root: this.state.root, 
            creationDate: new Date(), 
            folders: []
        }

        // Are we editing a connection that already exists? replace it, otherwise add it 
        connection.folders = this.state.configurations;
        connection.syncUser = this.state.syncUser; 
        connection.root = this.state.root;  

        if (existing)
            connections[connectionIdx] = connection;
        else
            connections.push(connection);

        // Update the state and add the connection 
        const sync = this.props.team?.sync ?? {};
        sync.desktop = connections;
        await this.props.updateTeam({ sync }) 

        this.props.modal(); 
        this.props.loaded(); 
    }

    renderContent = () => {
            return (
                <div
                    className='modal-content modal-center'
                >
                    <h1>Upload files securely from your computer</h1>
                    <p>
                        Stax·ai will automatically scan documents located in your specified file directories and securely transfer them to your selected location within your Stax·ai account.
                    </p>
                    <p>First, download the Stax.ai Desktop Sync app for your Operating System. Click on the executable and setup the app using the "Create a pathway for stax.ai to save files to your computer" option. Make sure you select the root directory as your sync location.</p>
                    <a style={{
                        display: 'inline-block',
                        width: 150,
                        height: 150,
                        textAlign: 'center',
                        fontSize: 18,
                    }} href="https://s3.us-west-1.amazonaws.com/stax.ai-public/apps/Stax.ai+Sync.exe" target="_blank" rel="noreferrer noopener">
                        <img style={{ margin: "15px auto" }} src="https://s3.us-west-1.amazonaws.com/stax.ai-public/gfx/download-button-win.png" alt="Download for Windows" />
                        Download for<br />
                        <strong>Windows</strong>
                    </a>
                    {/* <a  style={{
                        display: 'inline-block',
                        width: 150,
                        height: 150,
                        textAlign: 'center',
                        fontSize: 18,
                        margin: "0 30px"
                    }} href="https://s3.us-west-1.amazonaws.com/stax.ai-public/apps/Stax Desktop Sync App.dmg" target="_blank" rel="noreferrer noopener">
                        <img style={{ margin: "15px auto" }} src="https://s3.us-west-1.amazonaws.com/stax.ai-public/gfx/download-button-mac.png" alt="Download for Mac OS X" />
                        Download for<br />
                        <strong>Mac OS X</strong>
                    </a>
                    <a style={{
                        display: 'inline-block',
                        width: 150,
                        height: 150,
                        textAlign: 'center',
                        fontSize: 18,
                    }} href="https://s3.us-west-1.amazonaws.com/stax.ai-public/apps/Stax Desktop Sync App.AppImage" target="_blank" rel="noreferrer noopener">
                        <img style={{ margin: "15px auto" }} src="https://s3.us-west-1.amazonaws.com/stax.ai-public/gfx/download-button-ubuntu.png" alt="Download for Ubuntu" />
                        Download for<br />
                        <strong>Ubuntu</strong>
                    </a> */}
                    <br /><br /><br />

                
                    <div
                        className='config-form'
                    >
                        <h1>App Configuration</h1>
                        <div
                            className='config'
                        >
                            <label>Sync User</label>
                            <SelectUser 
                                value={this.state.syncUser??''}
                                onChange={user => this.setState({syncUser: user})}
                                users={this.props.team?.users}
                            /> 
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                    What user account is logged in to the Desktop Sync App installed on the device you want to upload files from?
                                </p>
                            </div>
                        </div>
                        <div
                            className='config'
                        >
                            <label>Root folder path</label>
                            <input
                                type={"text"}
                                className="config-field-string"
                                placeholder="X:/Clients"
                                value={this.state.root??''}
                                onChange={(e) => {
                                    this.setState({root: e.target.value})}
                                }    
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                    Provide the full folder path on your computer from where the desktop app should sync files from.
                                </p>
                            </div>
                        </div>

                        <h1>Source folders to scan</h1>
                        <div
                            style={{marginTop: 20, marginBottom: 20}}
                            key={Math.random()}
                        >
                            <Sortable 
                                title=''
                                data={this.state.configurations}

                                header={{
                                    name: {
                                        prop: 'source',
                                        label: 'Source folder'
                                    },
                                    meta: [
                                        {
                                            prop: '',
                                            label: 'Actions',
                                            width: 175,
                                            // width: ''
                                        },
                                    ]
                                }}

                                renderItem={(config, idx, renderMeta) => {
                                    return (
                                        <div
                                            className='sortable-list-item'
                                            key={idx}
                                            style={{ paddingLeft: 20 }}
                                        >
                                            <div
                                                className='sortable-list-name'
                                            >
                                                <img 
                                                    alt='folder'
                                                    src='https://static.stax.ai/images/icons/folder.png'
                                                    style={{ 
                                                        marginRight: 15, 
                                                        width: 30,
                                                        position: 'relative',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)'
                                                    }}
                                                />
                                                {config?.source?.length? config.source: "Root"}
                                            </div>

                                            <div
                                                className='stack-list-meta'
                                            >
                                                {renderMeta(
                                                    <div
                                                        className='import-connections-actions'
                                                    >
                                                        <div
                                                            className='import-connections-action'
                                                            style={{
                                                                marginLeft: 10,
                                                                backgroundImage: "url('https://static.stax.ai/images/icons/action-configure.png')"
                                                            }}
                                                            title="Edit connection configuration"
                                                            onClick={() => this.setState({edit: this.state.edit === idx? -1: idx, config: this.state.configurations[idx]})}
                                                        >
                                                            {this.state.edit === idx? "Cancel": "Edit"}
                                                        </div>
                                                        
                                                        <div
                                                            className='import-connections-action'
                                                            style={{
                                                                // marginLeft: 10,
                                                                backgroundImage: "url('https://static.stax.ai/images/icons/delete.png')"
                                                            }}
                                                            title="Edit connection configuration"
                                                            onClick={() => {

                                                                const configurations = [...this.state.configurations]; 
                                                                configurations.splice(idx, 1);

                                                                this.setState({
                                                                    configurations,
                                                                    edit: -1, 
                                                                    config: {}
                                                                })
                                                            }}
                                                        >
                                                            Remove
                                                        </div>
                                                    </div>, 194)}
                                            </div>
                                        </div>
                                    )
                                }}
                            />
                        </div>
                        {
                            this.state.edit < 0 &&
                            <div 
                                className="settings-add" 
                                style={{
                                    paddingTop: 20,
                                    paddingBottom: 20,
                                    margin: 0

                                }}
                                onClick={() => this.setState({edit: this.state.configurations.length, config: {}})}
                            >
                                ＋ Add a new folder to scan
                            </div>
                        }
                        {this.state.edit >= 0 && <>
                            <br /><br />   
                            <div
                                className='config'
                            >
                                <label>Folder path</label>
                                <input
                                    type={"text"}
                                    className="config-field-string"
                                    placeholder="/client/statements"
                                    value={this.state.config?.source??''}
                                    onChange={(e) => this.setState({config: {...this.state.config, source: e.target.value, name: e.target.value}})}    
                                />
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        Provide the relative folder path on your computer from where the desktop app is configured to sync files from. If left blank, we'll assume the root folder.
                                    </p>
                                </div>
                            </div>

                            <div
                                className='config'
                            >
                                <label>Destination stack</label>
                                <SelectStack 
                                    value={this.state.config?.destination??''}
                                    onChange={stack => this.setState({config: {...this.state.config, destination: stack}})}
                                    stacks={this.props.stacks}
                                /> 
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        Choose the stack where you want to upload files to.
                                    </p>
                                </div>
                            </div>

                            <div
                                className='config'
                            >
                                <label>Desktop file scan function</label>
                                <CodeEditor 
                                    className="config-code"
                                    style={{  }}
                                    theme={this.props.user && this.props.user.enableDarkMode ? "twilight" : "xcode"}
                                    value={this.state.config?.script?? SCAN_TEMPLATE}
                                    language='py'
                                    onChange={code => this.setState({config: {...this.state.config, script: code}})}
                                    width=''
                                /> 
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        Define a Python function to scan the folder and return a list of relative paths to files to upload. Leave it as is to upload all files in the folder.
                                    </p>
                                </div>
                            </div>

                            <div
                                className='config'    
                            >
                                <label>Desktop file cleanup function</label>
                                <CodeEditor 
                                    className="config-code"
                                    style={{  }}
                                    theme={this.props.user && this.props.user.enableDarkMode ? "twilight" : "xcode"}
                                    value={this.state.config?.cleanup?? CLEANUP_TEMPLATE}
                                    language='py'
                                    onChange={code => this.setState({config: {...this.state.config, cleanup: code}})}
                                    width=''
                                /> 
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        Define a Python function to cleanup any files after they have been uploaded into Stax.ai 
                                    </p>
                                </div>
                            </div>

                            <div
                                className='button'
                                style={{float: 'none', backgroundImage: 'url("https://static.stax.ai/images/icons/plus.png")'}}
                                onClick={() => {

                                    // Confirm everything is filled out. 

                                    if(!this.state.syncUser)
                                        return toast.error('You must select a user to sync with.');

                                    if(!this.state.config.destination)
                                        return toast.error('You must select a destination stack.');
                                    
                                    const configurations = [...this.state.configurations]
                                    const exists = this.state.edit < configurations.length; 

                                    // Has a source been set? if not set the name to 'Root'  
                                    this.state.config.name = this.state.config?.source?.length > 0? this.state.config.source: "Root"

                                    if(exists)
                                        configurations[this.state.edit] = this.state.config; 
                                    else
                                        configurations.push(this.state.config); 
                                    
                                    this.setState({
                                        configurations,
                                        edit: -1, 
                                        config: {}
                                    })
                                }}
                            >
                                {this.state.edit < this.state.configurations.length? "Update Configuration": "Add Configuration"}
                            </div>                 
                        </>
                    }

                    </div>
                    
                    <div style={{ clear: 'both' }} />
                    {this.state.edit < 0 && 
                        <input
                            className="import-config-button"
                            style={{
                                marginLeft: 0, 
                                float: 'none',
                                width: 220
                            }}
                            type="submit"
                            value="Save Configuration"
                            onClick={this.saveConfig}
                        />
                    }
                </div>
            )
    }

};

export default withRouter(ConnectDesktop);


